// IA des moines : poursuite BFS du coureur le plus proche, chute (mêmes règles
// que le coureur), mort et réapparition. Aucun import externe, aucune horloge,
// aucun RNG global : tout l'aléa passe par rngInt(s, …) sur l'état -> déterminisme.
// La collision moine/coureur est traitée dans sim.ts (checkCollisions).
import { digAt, isStableForLoose } from './dig'
import { hasSupport, isLadder, blocksFrom, isSolid, standsOnIce, tileAt } from './physics'
import { rngInt } from './rng'
import { closeHole, conveyorLift, conveyorPush, isLiftT, monkCaptifDe, trampolineOf } from './sim'
import { cellAt, centerOf, crateAt, GameState, Monk, Runner } from './state'
import { tryTeleport } from './teleport'
import { CAPTIF, FUSE_TICKS, type ItemKind, LURE_TICKS, MONK_RESPAWN_TICKS, STUN_TICKS_PLAYER, SUB, T, TAZER_RANGE, TRAP_TICKS, cellIndex, colOf, rowOf } from './types'

// MOINES ARMÉS (16/07, option de niveau monkPowers) : un moine piégé qui porte le MARTEAU se
// libère en 20 ticks au lieu de TRAP_TICKS (il casse la paroi) — et GARDE l'outil.
const MONK_JACK_ESCAPE = 20
/** Or lâché par le BOSS à sa dernière vie (31/07). Assez pour que le détour se sente, pas assez
 *  pour rendre le reste de la carte inutile — le pot total du niveau est mis à jour d'autant. */
export const BOSS_GOLD_REWARD = 3
// Objets qu'un moine armé peut ramasser : JAMAIS la clé (soft-lock des portes) ni la corde (inutile).
const MONK_PICKABLE: readonly ItemKind[] = ['bomb', 'jackhammer', 'tazer']

// Infranchissable pour le PATHFINDING d'un moine (Task 3 évite-lave) : un mur (isSolid) OU
// de la lave. Traiter la lave comme un mur dans le voisinage BFS fait qu'un moine la CONTOURNE
// au lieu d'y foncer. La chute FORCÉE (sans appui) tombe quand même dedans et meurt au second
// passage de stepMonks (comme le coureur) : seul le déplacement VOLONTAIRE l'évite. Sur un
// niveau sans lave, blockedForMonk === isSolid (tileAt ne renvoie jamais Lava) : zéro régression.
const blockedForMonk = (t: T): boolean => isSolid(t) || t === T.Lava || t === T.DeathZone || t === T.Fire // le moine ne marche jamais dans le feu (comme la lave) ; les PICS ne bloquent PAS la marche (dangereux qu'en chute)
// Version position-aware : ajoute les CAISSES (Phase 4), qui bloquent le moine sans être dans le
// terrain. Sans caisse, crateAt renvoie null -> strictement identique à blockedForMonk (zéro régression).
const blockedForMonkAt = (s: GameState, col: number, row: number): boolean =>
  blockedForMonk(tileAt(s, col, row)) || crateAt(s, col, row) !== null
// Tuile qui TUE un moine qui y arrive EN TOMBANT. Les pics s'y ajoutent (ils ne bloquent pas la
// marche — cf. décision D2 — mais empalent toute descente) : une arête de CHUTE vers eux est mortelle.
const deadlyFallForMonk = (t: T): boolean =>
  t === T.Lava || t === T.Fire || t === T.DeathZone || t === T.Spikes

// Voisins praticables d'une case pour le graphe de poursuite, avec les MÊMES
// prédicats que le coureur : sans support (et hors échelle/corde) la seule arête
// est la chute (col, row+1) ; sinon latéral non-bloqué, montée si échelle,
// descente non-bloquée (descente d'échelle, ou pas dans le vide qui deviendra chute).
// « bloqué » = mur OU lave (blockedForMonk) : le moine ne route jamais un chemin dans la lave.
function neighbors(s: GameState, cell: number): number[] {
  const col = colOf(cell, s.cols), row = rowOf(cell, s.cols)
  const here = tileAt(s, col, row)
  const probe = centerOf(cell, s.cols)
  if (!hasSupport(s, probe) && !isLadder(here) && here !== T.Rope) {
    // Sans appui, la seule arête est la CHUTE. AUDIT 16/07 : encore faut-il qu'elle ne soit pas un
    // suicide — le BFS routait joyeusement une chute dans la lave, le feu ou des pics (blockedForMonk
    // ne garde que le déplacement LATÉRAL), donc le moine se jetait dedans en « poursuivant ». Une
    // chute mortelle n'est pas un chemin : plus d'arête -> le BFS contourne (ou échoue, et le
    // columnFallback déjà gardé prend le relais). La chute PHYSIQUE, elle, ne change pas : un moine
    // poussé dans le vide au-dessus de la lave y tombe et meurt, comme avant.
    if (row + 1 >= s.rows || deadlyFallForMonk(tileAt(s, col, row + 1))) return []
    return [cellIndex(col, row + 1, s.cols)]
  }
  const out: number[] = []
  // SENS UNIQUE (17/07) : l'entrée à contresens est un mur pour le BFS aussi — le moine
  // route par ailleurs (ou renonce), exactement comme le coureur.
  if (col > 0 && !blockedForMonkAt(s, col - 1, row) && !blocksFrom(tileAt(s, col - 1, row), -1)) out.push(cellIndex(col - 1, row, s.cols))
  if (col + 1 < s.cols && !blockedForMonkAt(s, col + 1, row) && !blocksFrom(tileAt(s, col + 1, row), 1)) out.push(cellIndex(col + 1, row, s.cols))
  if (row > 0 && isLadder(here) && !blockedForMonkAt(s, col, row - 1)) out.push(cellIndex(col, row - 1, s.cols))
  if (row + 1 < s.rows && !blockedForMonkAt(s, col, row + 1)) out.push(cellIndex(col, row + 1, s.cols))
  // RAMPES (18/07, demande Claude-Alain « les moines doivent pouvoir monter les rampes ») : le moine
  // FRANCHIT une rampe en DIAGONALE, exactement comme le coureur (mêmes cases de passage), dans les
  // deux sens. Ces arêtes diagonales sont les SEULES du BFS -> chase les reconnaît (sr != row) pour
  // amorcer le hop. `freeM` = case ni mur, ni caisse, ni danger de marche (évite-lave inclus).
  const freeM = (c: number, r: number): boolean => c >= 0 && c < s.cols && r >= 0 && r < s.rows && !blockedForMonkAt(s, c, r)
  for (const dir of [-1, 1] as const) {
    // MONTÉE : une rampe orientée juste à côté -> on gravit vers son sommet (col+dir, row-1).
    const side = tileAt(s, col + dir, row)
    if (row > 0 && ((side === T.RampUpRight && dir === 1) || (side === T.RampUpLeft && dir === -1)) && freeM(col, row - 1) && freeM(col + dir, row - 1)) {
      out.push(cellIndex(col + dir, row - 1, s.cols))
    }
    // DESCENTE : debout au-dessus d'une rampe -> on dévale vers son pied (col+dir, row+1). '/' (droite)
    // se descend vers la GAUCHE, '\' (gauche) vers la DROITE — sens miroir de la montée (cf. coureur).
    const below = tileAt(s, col, row + 1)
    if (row + 1 < s.rows && ((below === T.RampUpRight && dir === -1) || (below === T.RampUpLeft && dir === 1)) && freeM(col + dir, row) && freeM(col + dir, row + 1)) {
      out.push(cellIndex(col + dir, row + 1, s.cols))
    }
  }
  return out
}

// BFS de `from` vers `to` (cap 500 nœuds), renvoie la PREMIÈRE case du chemin,
// ou -1 si inaccessible dans la limite.
function nextStep(s: GameState, from: number, to: number): number {
  if (from === to) return -1
  const prev = new Int16Array(s.cols * s.rows).fill(-1)
  prev[from] = from
  const queue: number[] = [from]
  let seen = 0
  // Plafond d'exploration mis a l'echelle de la grille (Lot E) : au moins 500 (back-compat 28x16
  // stricte : max(500, 448)=500), sinon le nombre total de cases -> le BFS peut couvrir toute une
  // grande carte (sans ce max, un chemin > 500 cases echouait et le moine cessait de poursuivre).
  const cap = Math.max(500, s.cols * s.rows)
  while (queue.length && seen < cap) {
    const c = queue.shift()!
    seen++
    for (const n of neighbors(s, c)) {
      if (prev[n] !== -1) continue
      prev[n] = c
      if (n === to) { let cur = n; while (prev[cur] !== from) cur = prev[cur]!; return cur }
      queue.push(n)
    }
  }
  return -1
}

// Skip de vitesse des moines (Task 3) : à quels ticks les moines NE bougent PAS. C'est le SEUL
// levier de vitesse (déterministe, fonction pure du tick) ; les réapparitions, elles, ne sautent
// jamais. `normal` = comportement HISTORIQUE inchangé bit à bit (skip tick%5===4, ~80 % de la
// vitesse du coureur) ; `rapide` = aucun skip (100 %) ; `lent` = 2 skips sur 5 (tick%5===4 ||
// tick%5===2, ~60 %). Défaut (valeur inattendue) -> normal, comme GameState.monkSpeed.
function monkSkipsThisTick(speed: GameState['monkSpeed'], tick: number): boolean {
  if (speed === 'rapide') return false
  if (speed === 'lent') return tick % 5 === 4 || tick % 5 === 2
  return tick % 5 === 4 // 'normal' (et tout défaut) : skip historique, inchangé
}

// Étape 5 du tick : réapparitions (jamais sautées), puis mouvement des moines vivants — SAUF
// aux ticks sautés par la vitesse du niveau (monkSkipsThisTick, cf. s.monkSpeed).
export function stepMonks(s: GameState) {
  const wasDead = s.monks.map((m) => m.respawnAt !== 0)
  // PICS (16/07) : « était en chute CE tick » par moine, capturé AVANT tout mouvement (fallMonk
  // remet falling=false à l'atterrissage dans le même tick). Local au tick, hors état.
  const wasFalling = s.monks.map((m) => m.falling)
  respawnMonks(s)
  // (Phase B, boss) Vitesse : le skip (monkSkipsThisTick) saute le MOUVEMENT des moines à certains
  // ticks — SAUF le boss « Moine Fou », qui agit à CHAQUE tick (il va vite). On calcule le skip une
  // fois et on saute chaque moine NON-boss dans la boucle (juste après les gardes sans effet, AVANT
  // climbOut/chute pour ne rien exécuter d'un moine sauté). Back-compat STRICTE : sans boss, un tick
  // sauté saute TOUS les moines ET les 2 passes ci-dessous, exactement comme l'ancien `return` global
  // (bit à bit) ; les gardes évalués avant le skip sont des lectures pures (aucun effet).
  const skip = monkSkipsThisTick(s.monkSpeed, s.tick)
  // RUSH de ligne de vue (MOINES ARMÉS 16/07) : un moine qui VOIT un coureur (même rangée,
  // vue dégagée) ignore les ticks sautés par la vitesse -> cadence « rapide » tant qu'il te
  // voit. Calculé UNE fois en début de tick (état pur) et réutilisé par les 3 passages
  // (mouvement, tapis, lave) pour rester bit à bit cohérent sur le même tick.
  const rushed = s.monks.map((m) => s.monkPowers && m.respawnAt === 0 && !m.dormant && monkSeesRunner(s, m))
  // EAU (décision D1 de l'audit 16/07) : un moine ne NAGE pas — il PATAUGE. Immergé, il n'agit qu'un
  // tick sur deux (déplacement ET chute à mi-vitesse) : il te suit dans le bassin, mais tu nages plus
  // vite que lui -> l'eau devient un vrai refuge... que le souffle rend temporaire. Avant, les moines
  // traversaient l'eau exactement comme de l'air pendant que toi tu te noyais. Ils ne se noient pas
  // (ils marchent au fond) : aucun nouveau champ d'état, aucun risque de désync. Calculé une fois en
  // début de tick comme `rushed`, et jamais true sur un niveau sans eau (zéro régression).
  const waded = s.monks.map((m) => {
    if (s.tick % 2 !== 0 || m.respawnAt !== 0) return false
    const t = tileAt(s, m.x >> 3, m.y >> 3)
    return t === T.Water || t === T.Bubbles
  })
  // PICS (décision D2) : altitude AVANT tout mouvement, par moine — même raisonnement que le coureur
  // (les pics blessent toute DESCENTE, pas la marche latérale).
  const prevY = s.monks.map((m) => m.y)
  // TAPIS (30/07) : abscisse AVANT tout mouvement, pour savoir si le pas que le moine vient de
  // faire s'oppose au tapis (cf. la passe conveyorPush plus bas). Toutes ses avancées propres
  // valent exactement 1 sous-unité (marche, hop de rampe, saut du sauteur) : un écart différent
  // de 1 n'est PAS un pas, c'est une téléportation — elle ne doit rien neutraliser.
  const prevX = s.monks.map((m) => m.x)
  for (let i = 0; i < s.monks.length; i++) {
    const m = s.monks[i]!
    if (m.dormant) continue                    // STATUE (gong d'or, 17/07) : scellée — ni mouvement, ni gravité, ni rien
    if (m.respawnAt !== 0) continue            // encore mort
    if (wasDead[i] === true) continue          // matérialisé CE tick : il agit au tick suivant
    if (m.emmure) continue                      // EMMURÉ dans la brique : totalement inerte
    if (m.lureUntil > s.tick) continue          // APPÂT (31/07) : il mordille, il ne fait rien d'autre
    // Le répit vient d'expirer : l'appât est CONSOMMÉ ici, et pas à la morsure — tant qu'il est
    // au sol il garde la meute braquée dessus, ce qui laisse au joueur le temps de refermer.
    if (m.lureCell >= 0) { libererAppat(s, m) }
    // GEL PENDANT LE REBOUCHAGE (31/07) : tant que le joueur cogne, le captif ne tente RIEN — ni
    // de remonter, ni d'être délivré. Le joueur qui s'engage n'est donc pas pris de vitesse au
    // dernier coup de pioche. Hors rebouchage, la minuterie tirée à l'entrée reprend son cours.
    if (m.trappedCell >= 0 && rebouchageEnCours(s, m.trappedCell)) continue
    if (m.trappedUntil === CAPTIF) { tenterDelivrance(s, m); continue } // sécurité : captif sans minuterie
    if (m.trappedUntil > s.tick) { tenterDelivrance(s, m); continue } // piégé dans un trou : immobile (Task 9)
    if (skip && m.kind !== 'boss' && !rushed[i]) continue // tick sauté par la vitesse : boss ET moines en rush agissent quand même
    if (waded[i]) continue // immergé : mi-vitesse (D1). Le boss aussi patauge — l'eau ne fait pas de favoritisme.
    // Étourdi (tazer) : aucune action VOLONTAIRE (jusqu'à stunUntil inclus)... mais la GRAVITÉ, elle,
    // ne s'arrête pas. AUDIT 16/07 : avant, un moine tazé flottait en l'air — creuser sous lui ne le
    // faisait pas tomber, donc le combo le plus naturel du jeu (« tase-le, creuse dessous, il est
    // piégé ») ne marchait tout simplement pas. Un moine déjà dans un trou (trappedCell) y reste.
    // TAPIS VERTICAL (31/07) : la case-centre PORTE, donc pas de chute — comme le coureur, qui
    // traite son tapis avant la gravité et n'en tombe jamais. Calculé ici parce que la branche
    // ÉTOURDI ci-dessous a sa propre gravité : sans ça, un moine tazé sur un tapis passait au
    // travers alors que le tapis est précisément ce qui devrait le retenir.
    // ⚠️ On ne touche pas à `hasSupport` : il sert AUSSI à décider où poser une bombe ou un appât,
    // et un tapis n'est pas un sol pour ça.
    const surTapis = isLiftT(tileAt(s, m.x >> 3, m.y >> 3))
    // ENROULÉ DANS LA TOILE (06/08) : il ne chasse plus, ne creuse plus, ne ramasse plus — il
    // se débat sur place cinq secondes. Traité comme l'étourdissement : la gravité continue
    // (un cocon sur un sol qu'on lui creuse tombe), mais plus aucune action.
    if (m.stunUntil >= s.tick || m.momieUntil > s.tick) {
      // VOL PLANÉ DU MAILLET (05/08) : un étourdi en plein bond GLISSE d'abord sa trajectoire
      // (chute suspendue), la gravité reprend à l'atterrissage. Corrige au passage le sauteur
      // tazé en plein saut, qui gelait en l'air puis reprenait son bond décalé d'une chute.
      if (m.hop > 0) { m.x += m.face; m.hop--; continue }
      if (!surTapis && m.trappedCell < 0 && (!hasSupport(s, m) || (m.falling && m.y % SUB !== 0))) fallMonk(s, m)
      continue
    }
    if (m.trappedCell >= 0) { climbOut(s, m); continue } // fin de blocage : remonte (Task 9)
    if (sinkInSand(s, m)) continue              // enfoncement dans le sable (Task 10) : rien d'autre
    // Phase B : le sauteur franchit un trou d'1 case (chute suspendue pendant le saut).
    // AUDIT 16/07 : un téléporteur au MILIEU du saut (seule case où x se retrouve aligné en plein vol)
    // téléportait le moine SANS éteindre son saut -> il repartait en glissade horizontale depuis
    // l'arrivée, à travers les murs (le hop suspend la chute ET les checks de mur). On compare la
    // case avant/après : tryTeleport ne renvoie rien, mais un déplacement se lit sur la position.
    // RAMPES (18/07) : franchissement diagonal en cours (amorcé par chase) — chute suspendue, comme
    // le saut du sauteur. Même garde téléporteur : téléporté en plein vol, on éteint le hop.
    if (tryRampHop(s, m)) {
      const before = cellAt(m.x, m.y, s.cols)
      tryTeleport(s, m)
      if (cellAt(m.x, m.y, s.cols) !== before) { m.rampHop = 0; m.rampDy = 0 }
      continue
    }
    if (trySauteurHop(s, m)) {
      const before = cellAt(m.x, m.y, s.cols)
      tryTeleport(s, m)
      if (cellAt(m.x, m.y, s.cols) !== before) m.hop = 0
      continue
    }
    // TRAMPOLINE (30/07) : le rebond PRIME sur la gravité, exactement comme chez le coureur, et
    // doit donc passer AVANT la branche de chute — sinon `fallMonk` (qui recale x sur la grille à
    // chaque tick) effacerait la dérive du trampoline directionnel dès la première sous-unité.
    if (m.bounce > 0) { bounceMonk(s, m); tryTeleport(s, m); continue }
    // Le tapis PORTE (cf. `surTapis` plus haut) : sans cette garde, `fallMonk` emporterait le moine
    // au tick même et le portage de la passe finale n'aurait jamais lieu.
    if (!surTapis && (!hasSupport(s, m) || (m.falling && m.y % SUB !== 0))) { fallMonk(s, m); tryTeleport(s, m); continue }
    m.falling = false
    if (tryRebuild(s, m)) continue // BÂTISSEUR (17/07) : chantier en cours, ou trou à reboucher devant lui
    chase(s, m)
    if (s.monkPowers) armedActions(s, m) // MOINES ARMÉS : ramassage / bombe / tazer, APRÈS le mouvement
    tryTeleport(s, m) // Task 12 : téléportation à l'arrivée au centre d'une case Teleporter
  }
  // Tapis roulant (tuiles P2 T2) : APRES tout deplacement, un moine vivant aligne et porte par un
  // tapis derive d'1 sous-unite dans le sens de la fleche (meme helper que le coureur). Second passage
  // comme la lave : couvre toutes les branches de mouvement ci-dessus sans dependre de leurs continue.
  // On EXCLUT les moines pieges/en cours de sortie de trou (trappedCell >= 0) : ancres, pas portes.
  // AVANT le check lave pour qu'un moine qui derive DANS la lave meure le tick meme. Deterministe.
  // Un moine sauté ce tick (skip, non-boss) n'a pas « agi » : il ne dérive donc pas (back-compat bit à bit).
  for (let i = 0; i < s.monks.length; i++) {
    const m = s.monks[i]!
    // Un bâtisseur EN CHANTIER (work > 0) est ancré comme un piégé : le tapis ne l'emporte pas
    // loin de son trou. Une STATUE (dormant) est scellée. work vaut 0 pour tous les autres types.
    if (m.respawnAt === 0 && m.trappedCell < 0 && !m.dormant && m.work === 0 && !waded[i] && (!skip || m.kind === 'boss' || rushed[i])) {
      const pas = m.x - prevX[i]!
      conveyorPush(s, m, Math.abs(pas) === 1 ? pas : 0) // seul un VRAI pas (1 su) neutralise le tapis
      // TAPIS VERTICAL (31/07) : même passe, même convention. Un moine qui a marché de son propre
      // chef ce tick sort du tapis (sa « flèche » à lui) ; sinon il est porté comme le coureur.
      conveyorLift(s, m, Math.abs(pas) === 1 ? pas : 0)
    }
  }
  // Lave (tuiles P1) : APRÈS tout déplacement, un moine vivant dont la case-centre est de
  // la lave meurt. Second passage volontaire : il couvre toutes les branches de mouvement
  // ci-dessus (chute, poursuite, sortie de trou...) sans dépendre de leurs `continue`. Un
  // niveau sans lave ne tue jamais (tileAt ne renvoie jamais Lava) : zéro régression. Un moine
  // sauté ce tick (skip, non-boss) n'a pas « agi » : il ne meurt donc pas en lave (back-compat bit à bit).
  for (let i = 0; i < s.monks.length; i++) {
    const m = s.monks[i]!
    if (m.respawnAt === 0 && !m.dormant && (!skip || m.kind === 'boss' || rushed[i])) { // la pierre d'une STATUE ne brûle pas
      const mt = tileAt(s, m.x >> 3, m.y >> 3)
      // Lave et feu BRÛLENT l'objet porté ; la zone mortelle et les pics tuent sans rien consumer :
      // l'objet retombe au sol (burns=false), récupérable — même anti-soft-lock que killMonk.
      if (mt === T.Lava || mt === T.Fire) killMonkInLava(s, m) // #1 + FEU : tuent aussi les moines
      else if (mt === T.DeathZone) killMonkInLava(s, m, false)
      // PICS (D2) : tout ce qui DESCEND dessus meurt (chute, échelle, tapis, saut de sauteur) ;
      // la marche latérale au travers reste sans danger. Même règle exactement que le coureur.
      else if (mt === T.Spikes && (m.falling || wasFalling[i] || m.y > prevY[i]!)) killMonkInLava(s, m, false)
    }
  }
}

// Un moine « voit »-il un coureur vivant ? Même rangée + vue dégagée (Bresenham). Sert le RUSH
// (cadence rapide) — pur, calculé en début de tick. (Le tazer refait son propre test à la volée.)
function monkSeesRunner(s: GameState, m: Monk): boolean {
  const col = m.x >> 3, row = m.y >> 3
  return s.runners.some((r) => r.alive && (r.y >> 3) === row &&
    hasLineOfSight(s, cellIndex(col, row, s.cols), cellIndex(r.x >> 3, row, s.cols)))
}

// Actions d'un moine ARMÉ (option monkPowers, 16/07), APRÈS son mouvement du tick. Un seul
// objet porté à la fois. Déterministe (ordre stable des runners/items, aucun RNG).
function armedActions(s: GameState, m: Monk): void {
  const col = m.x >> 3, row = m.y >> 3
  // Ramassage : au centre EXACT d'une case (comme le coureur), objets utiles seulement.
  if (m.item === null && m.x % SUB === 0 && m.y % SUB === 0) {
    const cell = cellIndex(col, row, s.cols)
    const it = s.items.find((i) => i.cell === cell && MONK_PICKABLE.includes(i.kind))
    if (it) {
      s.items = s.items.filter((i) => i !== it)
      m.item = it.kind
    }
  }
  if (m.item === 'bomb') {
    // Pose sa bombe quand un coureur vivant est ACCULÉ à <= 2 cases (Manhattan). Le moine
    // continue sa poursuite : à lui d'être ailleurs à l'explosion (chaos assumé, équitable).
    const near = s.runners.some((r) => r.alive && !(r.deguiseUntil > s.tick) && Math.abs((r.x >> 3) - col) + Math.abs((r.y >> 3) - row) <= 2) // un faux moine (05/08) n'affole pas le poseur de bombe
    const cell = cellIndex(col, row, s.cols)
    if (near && !s.bombs.some((b) => b.cell === cell)) {
      s.bombs.push({ cell, explodeAt: s.tick + FUSE_TICKS })
      s.events.push({ type: 'bombPlaced', cell })
      m.item = null
    }
  } else if (m.item === 'tazer') {
    // UN tir : coureur vivant non étourdi, même rangée, à portée, vue dégagée -> étourdi.
    // AUDIT 16/07 : un coureur EN DASH est invulnérable (lave, moines, bombes, chute...) — le tazer
    // du moine le figeait pourtant net, ce qui était la pire des morts : gelé au milieu du danger
    // qu'il traversait. Il le rate maintenant, comme tout le reste (et le moine garde son tazer).
    for (const r of s.runners) {
      if (!r.alive || r.stunUntil >= s.tick || (r.y >> 3) !== row || r.dashUntil > s.tick) continue
      if (Math.abs((r.x >> 3) - col) > TAZER_RANGE) continue
      if (!hasLineOfSight(s, cellIndex(col, row, s.cols), cellIndex(r.x >> 3, row, s.cols))) continue
      r.stunUntil = s.tick + STUN_TICKS_PLAYER // cible = JOUEUR -> 90 t (3 s)
      s.events.push({ type: 'taze', from: cellIndex(col, row, s.cols), to: cellIndex(r.x >> 3, row, s.cols) })
      m.item = null
      break
    }
  }
}

// Sable mouvant (Task 10), MÊME règle que le coureur : dès que la case-centre du moine
// est du sable, il s'enfonce d'1 sous-unité tous les 2 ticks (tant que la case dessous
// est praticable ; sinon il repose) et ne fait RIEN d'autre. Renvoie true si géré.
function sinkInSand(s: GameState, m: Monk): boolean {
  if (tileAt(s, m.x >> 3, m.y >> 3) !== T.Sand) return false
  m.falling = false
  if (s.tick % 2 === 0 && !isSolid(tileAt(s, m.x >> 3, (m.y >> 3) + 1))) { m.x &= ~7; m.y += 1 }
  return true
}

// Saut du moine SAUTEUR (Phase B) : au bord d'un trou/vide d'1 case dans la direction du coureur,
// il le FRANCHIT au lieu d'y tomber (un moine `normal` tombe, inchangé). PUREMENT déterministe
// (aucun RNG) : le saut couvre 2 cases (par-dessus le trou jusqu'à l'atterrissage) et se déroule
// en 2*SUB sous-unités horizontales ; pendant m.hop > 0 la chute est suspendue (le caller fait
// `continue`). Renvoie true si un saut est en cours ou vient d'être amorcé, false sinon.
//   - m.hop > 0        : saut en cours -> avance d'1 sous-unité dans m.face, décrémente, true.
//   - amorçage (sauteur, aligné, posé, direction vers un trou franchissable) : m.hop = 2*SUB.
// Garde-mur : la case d'atterrissage (col+2dir) doit être non bloquée (ni mur ni lave) ET portée ;
// hors grille -> tileAt renvoie Concrete (solide) -> pas d'amorçage. Un non-sauteur renvoie
// toujours false et garde m.hop = 0 : zéro régression pour normal/voleur/boss.
// RAMPES (18/07) : DÉROULÉ d'un franchissement diagonal en cours (miroir du hop du coureur). 1 su
// horizontale (m.face) + 1 su verticale (m.rampDy) par tick, SUB ticks = 1 case ; chute suspendue
// (le caller fait `continue`). L'AMORÇAGE, lui, vit dans chase (il dépend du pas choisi par le BFS).
function tryRampHop(s: GameState, m: Monk): boolean {
  if (m.rampHop <= 0) return false
  m.x += m.face; m.y += m.rampDy; m.rampHop--
  if (m.rampHop === 0) m.rampDy = 0 // arrivée alignée : plus de sens vertical en attente
  return true
}

function trySauteurHop(s: GameState, m: Monk): boolean {
  if (m.hop > 0) { m.x += m.face; m.hop--; return true } // saut en cours : chute suspendue
  if (m.kind !== 'sauteur') return false
  if (m.x % SUB !== 0 || m.y % SUB !== 0 || !hasSupport(s, m)) return false // amorce seulement aligné et posé
  const target = nearestRunnerCell(s, m)
  if (target < 0) return false
  const col = m.x >> 3, row = m.y >> 3
  const rc = colOf(target, s.cols)
  if (rc === col) return false
  const dir: -1 | 1 = rc > col ? 1 : -1
  const midCol = col + dir, landCol = col + 2 * dir
  // Trou d'1 case franchissable : case du milieu VIDE sans appui (un normal y tomberait) ET case
  // d'atterrissage NON bloquée AVEC appui dessous. Les DEUX cases utilisent blockedForMonk (ni mur
  // ni lave) : un saut est un mouvement VOLONTAIRE, il ne doit donc JAMAIS transiter/atterrir dans
  // la lave (même invariant que l'évite-lave A3a ; sinon le second passage lave tuerait le sauteur
  // en plein vol). hasSupport testé au centre exact de chaque case.
  const midHole = !blockedForMonkAt(s, midCol, row) && !hasSupport(s, { x: midCol * SUB, y: row * SUB })
  const landOk = !blockedForMonkAt(s, landCol, row) && hasSupport(s, { x: landCol * SUB, y: row * SUB })
  if (!midHole || !landOk) return false
  m.hop = 2 * SUB
  m.face = dir
  m.x += m.face
  m.hop--
  return true
}

// --- MOINE BÂTISSEUR (17/07, lot « la meute vit ») ------------------------------------------
// Truelle en main, il REBOUCHE les trous creusés : le contre direct de l'arme du coureur.
// Durée d'un chantier : ~1 s. Le rebouchage passe par closeHole, la mécanique EXACTE de la
// repousse naturelle (éjections d'or/objets/caisses, ensevelissement, recalages) — seul le
// moment change.
const REBUILD_TICKS = 30

// Comportement du bâtisseur, appelé posé et hors chute, AVANT chase. Renvoie true si le moine
// est occupé (chantier en cours ou démarré) : il ne chasse pas ce tick.
//   - Chantier en cours (work > 0) : décompte ; à zéro, referme le trou visé (workCell) s'il est
//     encore ouvert (la repousse naturelle a pu passer avant lui : il range juste sa truelle).
//   - Sinon, ALIGNÉ au centre d'une case : un trou ouvert dans le SOL devant lui (col+face, row+1),
//     ou derrière, démarre un chantier — le contre exact du piège classique (trou creusé sur sa
//     route). Il se tourne vers son chantier, s'immobilise, event `rebuild` (toc de truelle).
// Un bâtisseur qui tombe DANS un trou par le haut reste piégé comme les autres (équilibre
// préservé : le piéger LUI reste possible) ; un non-bâtisseur renvoie toujours false (work
// reste 0 partout ailleurs) : zéro régression.
function tryRebuild(s: GameState, m: Monk): boolean {
  if (m.kind !== 'batisseur') return false
  if (m.work > 0) {
    m.work--
    if (m.work === 0) {
      const h = s.holes.find((x) => x.cell === m.workCell)
      if (h) closeHole(s, h)
      m.workCell = -1
    }
    return true
  }
  if (m.x % SUB !== 0 || m.y % SUB !== 0) return false
  const col = m.x >> 3, row = m.y >> 3
  if (row + 1 >= s.rows) return false
  for (const dir of [m.face, -m.face as -1 | 1]) {
    const c = col + dir
    if (c < 0 || c >= s.cols) continue
    const cell = cellIndex(c, row + 1, s.cols)
    if (!s.holes.some((h) => h.cell === cell)) continue
    // TROU-PRISON (31/07) : il ne rebouche JAMAIS sur un congénère captif. Sans ce garde, le
    // bâtisseur enterrait ses propres camarades — et surtout il annulait tout seul le piège que
    // le joueur venait de tendre, ce qui rendait la prison inutilisable sur une carte à bâtisseur.
    if (monkCaptifDe(s, cell)) continue
    m.face = dir // il se tourne vers son chantier
    m.work = REBUILD_TICKS
    m.workCell = cell
    s.events.push({ type: 'rebuild', cell })
    return true
  }
  return false
}

// --- GONG D'OR : les moines-statues (17/07) --------------------------------------------------
// Grâce au réveil : la statue « s'étire » (stunUntil) — immobile ET inoffensive (checkCollisions
// ignore les étourdis) — pour qu'un or ramassé à côté d'elle ne soit JAMAIS une mort surprise.
// 1 s : la hitbox élargie du ramassage peut laisser le coureur DANS la case de la statue ; il a
// largement le temps d'en sortir (3+ cases de marche) avant qu'elle ne devienne dangereuse.
const GONG_WAKE_TICKS = 30

/** Éveille toute statue ADJACENTE (Chebyshev <= 1) à l'or ramassé en `goldCell`. Appelé aux DEUX
 *  sites de collecte (coureur : collectAt ; moine : chase) : le gong protège l'or, peu importe la
 *  main qui le prend. lastSeen = la case de l'or : sous smartMonks, la statue va enquêter là. */
export function wakeStatues(s: GameState, goldCell: number): void {
  const gc = colOf(goldCell, s.cols), gr = rowOf(goldCell, s.cols)
  for (const m of s.monks) {
    if (!m.dormant) continue
    if (Math.abs((m.x >> 3) - gc) > 1 || Math.abs((m.y >> 3) - gr) > 1) continue
    m.dormant = false
    m.stunUntil = s.tick + GONG_WAKE_TICKS
    m.lastSeen = goldCell
    s.events.push({ type: 'gong', cell: cellAt(m.x, m.y, s.cols) })
  }
}

// --- ÉMOTIONS (17/07) : ce que « pense » un moine, pour le rendu ----------------------------
// Fonction PURE dérivée de l'état (aucune mémoire propre, aucun RNG) : le client l'appelle à
// chaque frame, les 3 écrans d'un multi affichent donc la même chose. Réservée à l'IA MALIGNE
// (smartMonks) : sans elle il n'y a ni ligne de vue ni lastSeen, un signe mentirait.
//   'alert'  (« ! ») : il VOIT un coureur vivant en ce moment même.
//   'search' (« ? ») : plus de vue, mais une dernière position mémorisée (il enquête).
//   'idle'   (« z ») : patrouille sans but (jamais vu, ou piste épuisée).
export function monkMood(s: GameState, m: Monk): 'alert' | 'search' | 'idle' {
  const runner = nearestRunner(s, m)
  if (runner && hasLineOfSight(s, cellAt(m.x, m.y, s.cols), cellAt(runner.x, runner.y, s.cols))) return 'alert'
  return m.lastSeen >= 0 ? 'search' : 'idle'
}

// Chute forcée, MÊME arbitrage d'atterrissage que le coureur : on n'atterrit
// qu'aligné ; si un support apparaît en cours de chute (ex. brique repoussée sous
// le moine) on recale vers le haut et on stoppe — jamais figé désaligné.
function fallMonk(s: GameState, m: Monk) {
  if (hasSupport(s, m) && m.y % SUB !== 0) { m.y &= ~7; m.falling = false; return }
  m.falling = true
  m.x &= ~7 // recalage PLANCHER : préserve la case-centre (même arbitrage que fallOneSub)
  m.y += 1
  if (m.y % SUB === 0) {
    const cell = cellAt(m.x, m.y, s.cols)
    // Le boss « Moine Fou » TRAVERSE les trous comme le coureur (il creuse pour descendre vers sa
    // cible, cf. bossDig) : il n'est jamais piégé. Les autres moines y sont piégés (mécanique de jeu
    // inchangée — back-compat stricte : m.kind !== 'boss' garde le comportement normal/sauteur/voleur).
    if (m.kind !== 'boss' && s.holes.some((h) => h.cell === cell)) enterHole(s, m, cell) // piégeage : rempli en Task 9
    // corde rattrapée OU support atteint : la chute s'arrête (réévaluée au prochain tick).
    if (tileAt(s, m.x >> 3, m.y >> 3) === T.Rope || hasSupport(s, m)) {
      m.falling = false
      // TRAMPOLINE (30/07) : même déclencheur que le coureur — on rebondit en ATTERRISSANT d'une
      // chute, jamais en marchant sur la tuile. Un moine piégé dans un trou (trappedCell) vient
      // d'être capturé au-dessus : il ne rebondit pas, le piège prime.
      if (m.trappedCell < 0) {
        const tramp = trampolineOf(tileAt(s, m.x >> 3, (m.y >> 3) + 1))
        if (tramp) {
          m.bounce = tramp.sub
          m.bounceDir = tramp.dir
          s.events.push({ type: 'monkBounce', cell: cellAt(m.x, m.y, s.cols) })
        }
      }
    }
  }
}

// Rebond du moine sur un trampoline : montée de 2 sous-unités/tick, interrompue par un plafond
// solide, avec la dérive latérale du trampoline DIRECTIONNEL. Miroir de la branche `r.bounce > 0`
// du coureur, à une différence près, VOULUE (demande Claude-Alain : « direction de la chute
// dirigeable seulement sur trampo pour les moines ») : le moine n'a pas d'input, sa seule dérive
// possible vient donc du trampoline. Partout ailleurs sa chute reste strictement verticale.
//
// ⚠️ LE RECALAGE `m.x &= ~7` DE FIN DE REBOND EST GARDÉ ICI, ALORS QU'IL A ÉTÉ RETIRÉ CHEZ LE
// COUREUR (06/08, cf. `alignSiPorte` dans sim.ts). CHOIX DÉLIBÉRÉ, pas un oubli de synchronisation
// — et la question méritait d'être posée, ce dépôt ayant déjà payé « deux copies d'une règle
// finissent par diverger » (c'est pourquoi `trampolineOf` est partagée).
//
// La raison est que les deux chutes ne se ressemblent pas. Celle du coureur sait voler désalignée
// et se recale à l'ARRIVÉE (`alignXLand`) : son invariant est donc tenu ailleurs, et le recalage du
// sommet n'était qu'un doublon visible. Le moine, lui, n'a pas d'équivalent d'`alignXLand`, sa
// chute est verticale et son support se juge sur DEUX BORDS (`hasSupport`) : c'est ce recalage-ci
// qui garantit qu'il retombe dans une colonne franche — donc qu'il tombe VRAIMENT dans les trous
// creusés, ce dont dépend tout le piégeage. L'à-coup rapporté était celui du joueur ; un moine qui
// se recale en l'air ne gêne personne, un moine qui rate les trous casse le jeu.
function bounceMonk(s: GameState, m: Monk): void {
  const col = m.x >> 3, row = m.y >> 3
  if (m.y % SUB === 0 && isSolid(tileAt(s, col, row - 1))) { m.bounce = 0; m.x &= ~7; return } // plafond
  m.y -= 2
  m.bounce -= 2
  m.falling = false
  if (m.bounce <= 0) { m.bounce = 0; m.bounceDir = 0; m.x &= ~7; return } // fin : réaligné avant de rendre la main
  if (m.bounceDir !== 0) {
    m.face = m.bounceDir
    // Même garde d'emprise que le coureur : le pas latéral est refusé s'il chevauche un mur ou une
    // caisse, pour que le rebond longe les parois au lieu de s'y encastrer.
    const nx = m.x + m.bounceDir, rr = m.y >> 3
    const okCol = (nc: number): boolean => !blocksFrom(tileAt(s, nc, rr), m.bounceDir as -1 | 1) && !crateAt(s, nc, rr)
    if (okCol(nx >> 3) && okCol((nx + SUB - 1) >> 3)) m.x += m.bounceDir
  }
}

// Poursuite : au centre d'une case on recalcule le pas BFS (avec 1/8 d'imperfection
// « humaine » et anti-empilement), puis on avance d'une sous-unité vers la case visée.
function chase(s: GameState, m: Monk) {
  // Voleur CHARGE (Phase B) : au lieu de viser le coureur, il vise une case de FUITE et s'en
  // eloigne. Le voleur en fuite fuit TOUJOURS (smartMonks n'y change rien). Sinon : cible « maligne »
  // (anticipation + ligne de vue + patrouille) SI smartMonks est actif, sinon cible = coureur le plus
  // proche, INCHANGE (back-compat stricte bit a bit : quand smartMonks est off, aucune branche maligne
  // ne s'execute). La cible choisie passe ensuite dans le meme BFS / imperfection / moveToward.
  // APPÂT (31/07) : il PRIME sur tout le reste, y compris sur la fuite du voleur — c'est ce qui
  // en fait un outil fiable. Sans cette priorité absolue, poser un appât sous le nez d'un moine
  // qui te poursuit ne changerait rien et l'objet ne servirait à rien.
  // SPECTRE de la MEUTE (05/08) : il ne connaît QUE sa proie désignée. Il IGNORE l'appât (un
  // fantôme ne mange pas) et l'IA maligne (il n'a rien à anticiper : il sait déjà où aller).
  const target = m.spectre
    ? nearestRunnerCell(s, m)
    : appatVise(s, m) ?? (m.kind === 'voleur' && m.carried
      ? fleeTargetCell(s, m)
      : s.smartMonks
        ? smartTargetCell(s, m)
        : nearestRunnerCell(s, m))
  // Sans cible : si des coureurs VIVANTS existent mais qu'aucun n'est chassable (tous déguisés,
  // 05/08), le moine PATROUILLE — il vit, il ne se fige pas. Tous morts : inertie HISTORIQUE
  // (seule la dérive d'un tapis le porte, contrat des tests de tuiles).
  if (target < 0) { if (s.runners.some((r) => r.alive)) patrouille(s, m); return }
  const myCell = cellAt(m.x, m.y, s.cols)
  const atCenter = m.x % SUB === 0 && m.y % SUB === 0
  if (atCenter) {
    // Ramassage d'or par le moine (Task 9) : au centre, AVANT le choix de pas.
    // Mains libres et aucun coureur vivant sur la case (le coureur, prioritaire, a
    // déjà agi ce tick). Les moines ne ramassent JAMAIS les objets.
    // ⚠️ Un SPECTRE de la meute (05/08) ne ramasse rien : il est là pour une seule chose.
    const gi = s.golds.indexOf(myCell)
    if (gi >= 0 && !m.carried && !m.spectre && !s.runners.some((r) => r.alive && cellAt(r.x, r.y, s.cols) === myCell)) {
      m.carried = true
      s.golds.splice(gi, 1)
      s.events.push({ type: 'gold', by: 'monk', cell: myCell })
      wakeStatues(s, myCell) // GONG D'OR : l'or quitte le socle, peu importe la main qui le prend
    }
    // Arrivé SUR l'appât : il mord. Il s'immobilise LURE_TICKS, et l'appât est consommé à la fin
    // de ce répit (pas tout de suite) : sinon les autres moines perdraient leur cible en route et
    // se retourneraient vers le coureur avant même que le piège soit refermé.
    // ⚠️ UN FANTÔME NE MANGE PAS (05/08) : le spectre de la meute traverse l'appât sans le voir.
    // Le bypass du choix de CIBLE ne suffisait pas — il mordait en marchant dessus par hasard.
    if (!m.spectre && s.appats.includes(myCell)) {
      m.lureUntil = s.tick + LURE_TICKS
      m.lureCell = myCell
      m.pathNextCell = myCell
      s.events.push({ type: 'appatMordu', cell: myCell })
      return
    }
    let step = nextStep(s, myCell, target)
    // Boss creuseur (« Moine Fou ») : quand la poursuite normale échoue (BFS sans route — la cible est
    // barrée par un sol/mur de briques), le boss creuse la brique dessous-avant vers elle pour se
    // frayer un passage vers le bas et descendre à travers le trou (il les traverse, cf. fallMonk).
    // UNIQUEMENT quand le BFS échoue -> borné, pas de creusage destructeur en boucle. Déterministe
    // (aucun RNG ; on retourne AVANT le tirage d'imperfection, qui de toute façon ne court que si step>=0).
    if (step < 0 && m.kind === 'boss' && bossDig(s, m, target)) { m.pathNextCell = myCell; return }
    const ns = neighbors(s, myCell)
    if (step >= 0 && ns.length > 1 && rngInt(s, 8) === 0) {
      const alt = ns.find((n) => n !== step) // imperfection : prend un autre voisin praticable
      if (alt !== undefined) step = alt
    }
    if (step < 0) step = columnFallback(s, myCell, target) // BFS échoue : se rapprocher de la colonne du coureur
    if (step < 0) { m.pathNextCell = myCell; return } // aucune issue praticable : immobile
    // anti-empilement : ne pas viser la case-centre d'un autre moine actif non piégé
    if (s.monks.some((o) => o !== m && o.respawnAt === 0 && o.trappedCell < 0 && cellAt(o.x, o.y, s.cols) === step)) {
      m.pathNextCell = myCell; return
    }
    m.pathNextCell = step
  }
  // RAMPES (18/07) : un pas du BFS en DIAGONALE (sr != row) ne vient QUE d'une arête de rampe
  // (neighbors) -> on amorce le franchissement diagonal verrouillé (miroir exact du hop du coureur),
  // une seule fois, depuis le centre. rampDy donne le sens vertical (montée -1 / descente +1) ; le
  // déroulé (tryRampHop, avant chase) fait le reste, chute suspendue jusqu'à l'arrivée alignée.
  if (atCenter && m.rampHop === 0 && m.pathNextCell >= 0) {
    const sc = colOf(m.pathNextCell, s.cols), sr = rowOf(m.pathNextCell, s.cols)
    const mc = m.x >> 3, mr = m.y >> 3
    if (sc !== mc && sr !== mr) {
      m.face = sc > mc ? 1 : -1
      m.rampDy = sr > mr ? 1 : -1
      m.rampHop = SUB
      m.x += m.face; m.y += m.rampDy; m.rampHop--
      return
    }
  }
  // 🚨 LE MOINE PLANTÉ ENTRE DEUX CASES (06/08) — signalé par Claude-Alain : « quand un moine se
  // prend dans la momie il reste bloqué et ne bouge plus ».
  //
  // Le pas mémorisé (`pathNextCell`) ne se recalcule QU'AU CENTRE d'une case, et `moveToward`
  // refuse d'agir sans pas connu (`cell < 0`). Or plusieurs situations l'effacent — la bandelette
  // en est une — et si elles surprennent le moine À CHEVAL sur deux cases, il ne peut plus ni
  // avancer (pas de pas) ni atteindre un centre (il n'avance pas) : l'immobilité est DÉFINITIVE.
  // Ni mort, ni piégé, ni rien qui l'explique à l'écran.
  //
  // La sortie de secours est la plus simple qui soit : sans pas connu et hors du centre, on vise
  // le centre de SA PROPRE case. Il finit le pas entamé, retrouve un centre au tick suivant, et
  // la poursuite normale reprend la main. Placé ici plutôt qu'au moment de la libération : les
  // trois endroits qui posent `-1` sont ainsi couverts d'un coup, y compris ceux à venir.
  if (m.pathNextCell < 0 && (m.x % SUB !== 0 || m.y % SUB !== 0)) m.pathNextCell = cellAt(m.x, m.y, s.cols)
  moveToward(s, m, m.pathNextCell)
}

/** L'appât visé par ce moine : le plus proche à vol d'oiseau (départage par index de case, pour
 *  que deux clients choisissent le MÊME en cas d'égalité). null s'il n'y en a aucun. */
function appatVise(s: GameState, m: Monk): number | null {
  if (s.appats.length === 0) return null
  const col = m.x >> 3, row = m.y >> 3
  let best = -1, bestD = Infinity
  for (const cell of s.appats) {
    const d = Math.abs(colOf(cell, s.cols) - col) + Math.abs(rowOf(cell, s.cols) - row)
    if (d < bestD) { bestD = d; best = cell }
  }
  return best
}

// Creusage du boss (« Moine Fou ») vers une cible située PLUS BAS et inaccessible par le BFS : si une
// brique barre la descente dessous-avant (col + direction de la cible, row+1), la creuser (digAt,
// EXACTEMENT comme le coureur) ouvre un trou par lequel le boss descendra (il traverse ses propres
// trous, cf. fallMonk). Conditions : cible réellement plus bas (creuser vers le bas la rapproche),
// brique à creuser dessous-avant, et — pour une descente en diagonale (dir != 0) — case d'engagement
// (col+dir, row) libre (non solide). Descente droite (dir == 0, cible dans la même colonne) : on creuse
// la case juste dessous. Purement déterministe (aucun RNG). Renvoie true si un creusage a eu lieu.
function bossDig(s: GameState, m: Monk, target: number): boolean {
  const col = m.x >> 3, row = m.y >> 3
  if (rowOf(target, s.cols) <= row) return false // cible pas plus bas : creuser vers le bas n'aide pas
  const rc = colOf(target, s.cols)
  const dir = rc > col ? 1 : rc < col ? -1 : 0
  if (tileAt(s, col + dir, row + 1) !== T.Brick) return false // rien à creuser dessous-avant
  if (dir !== 0 && isSolid(tileAt(s, col + dir, row))) return false // pas de place pour s'engager en diagonale
  digAt(s, cellIndex(col + dir, row + 1, s.cols))
  return true
}

// Repli quand le BFS échoue (coureur inaccessible) : un pas horizontal vers la
// COLONNE du coureur si la case latérale est franchissable, sinon -1 (immobile).
// « franchissable » = ni mur ni lave (blockedForMonk) : sans ce garde, un moine
// coincé au bord d'une nappe de lave (BFS en échec) y foncerait par ce repli.
function columnFallback(s: GameState, from: number, target: number): number {
  const col = colOf(from, s.cols), row = rowOf(from, s.cols)
  const dir = colOf(target, s.cols) > col ? 1 : colOf(target, s.cols) < col ? -1 : 0
  const side = col + dir
  if (dir !== 0 && side >= 0 && side < s.cols && !blockedForMonkAt(s, side, row) && !blocksFrom(tileAt(s, side, row), dir) && !stepEndsDeadly(s, side, row)) { // sens unique respecté (17/07), comme le BFS
    return cellIndex(side, row, s.cols)
  }
  return -1
}

// Poser le pied en (col,row) condamne-t-il le moine ? La case elle-même est déjà filtrée par
// blockedForMonkAt (mur/lave/feu/zone) ; ici on regarde ce qui arrive SI ELLE NE PORTE PAS : la
// chute qui suit traverse-t-elle une tuile mortelle ? AUDIT 16/07 : sans ça, un moine dont le BFS
// échoue (coureur de l'autre côté d'un lac de lave) se rabattait sur « avance vers sa colonne » et
// marchait dans le vide au-dessus de la lave — un suicide qu'aucun joueur n'a provoqué. Le BFS, lui,
// est déjà protégé par ses arêtes de chute (neighbors) : la transitivité fait le reste.
function stepEndsDeadly(s: GameState, col: number, row: number): boolean {
  if (hasSupport(s, { x: col * SUB, y: row * SUB }) || isLadder(tileAt(s, col, row))) return false // il tient debout : rien à craindre
  for (let r = row + 1; r < s.rows; r++) {
    if (deadlyFallForMonk(tileAt(s, col, r))) return true
    if (hasSupport(s, { x: col * SUB, y: r * SUB })) return false // il atterrit ici, sain et sauf
  }
  return false // sort par le bas : un moine n'a pas de « chute hors écran » (contrairement au coureur)
}

// Avance d'une sous-unité vers le centre de `cell` (horizontal prioritaire, comme
// le mouvement du coureur : on aligne x avant de bouger en y). Sur la GLACE (Task 10)
// le moine glisse : son déplacement horizontal est FORCÉ dans sa direction `face`
// (même arrêt-au-mur que le coureur), en ignorant la case du BFS. Le BFS reste
// « ice-naïf » (glace = sol normal) : l'imprécision qui en résulte est un charme du
// jeu, pas un bug (arbitrage verrouillé).
function moveToward(s: GameState, m: Monk, cell: number) {
  if (standsOnIce(s, m)) {
    const targetCol = (m.x >> 3) + m.face
    if (m.x % SUB === 0 && (blockedForMonkAt(s, targetCol, m.y >> 3) || blocksFrom(tileAt(s, targetCol, m.y >> 3), m.face))) return // mur OU lave OU caisse OU clapet à contresens : glissade finie
    m.x += m.face
    return
  }
  if (cell < 0) return // sécurité : aucun pas connu (ne devrait pas arriver hors init)
  const { x: tx, y: ty } = centerOf(cell, s.cols)
  if (m.x !== tx) { m.face = m.x < tx ? 1 : -1; m.x += m.face }
  else if (m.y !== ty) m.y += m.y < ty ? 1 : -1
}

// Coureur vivant le plus proche (distance de Manhattan en cases), ou null. Départage INCHANGÉ :
// le PREMIER coureur à distance minimale dans l'ordre de s.runners (comparaison stricte `d < bd`).
// L'IA maligne s'en sert pour lire aussi la direction (r.face) du coureur visé (anticipation).
// PATROUILLE DE REPLI (05/08, retour Claude-Alain : « lorsque je me transforme en moine tous
// les moines se stoppent »). Plus aucune proie (tous les coureurs déguisés, ou morts) : les
// moines ne se FIGENT plus — ils vont et viennent, marche dans leur sens et demi-tour au mur
// ou au bord du vide. C'est ce qui rend le déguisement crédible : « un moine parmi les
// moines » exige des moines qui vivent. Déterministe : zéro RNG, zéro cible, fonction pure de
// la grille. Un moine en pleine échelle (y désaligné) attend sur place, accroché.
function patrouille(s: GameState, m: Monk): void {
  if (m.y % SUB !== 0) return
  if (m.x % SUB !== 0) { m.x += m.face; return } // finit proprement le pas engagé
  const devant = (m.x >> 3) + m.face
  const row = m.y >> 3
  if (!blockedForMonkAt(s, devant, row) && hasSupport(s, { x: devant << 3, y: m.y })) m.x += m.face
  else m.face = m.face === 1 ? -1 : 1
}

function nearestRunner(s: GameState, m: Monk): Runner | null {
  // MEUTE PRIVÉE (05/08) : un SPECTRE ne connaît qu'une proie, celle qu'on lui a désignée —
  // et il voit à travers les robes (le déguisé ne lui échappe pas).
  if (m.spectre) {
    const c = s.runners[m.cible]
    return c && c.alive ? c : null
  }
  // COURONNE MAUDITE (05/08) : le porteur est la cible PRIORITAIRE de tous les moines réels,
  // où qu'il soit. Trois étages de tentation, tous lisibles : appât (traité par appatVise, il
  // prime sur tout) > couronne > poursuite normale du plus proche.
  const maudit = s.runners.find((r) => r.alive && r.couronne && !(r.deguiseUntil > s.tick))
  if (maudit) return maudit
  let best: Runner | null = null, bd = 1e9
  for (const r of s.runners) {
    if (!r.alive) continue
    if (r.deguiseUntil > s.tick) continue // DÉGUISEMENT (05/08) : un faux moine n'est pas une proie
    const d = Math.abs((r.x >> 3) - (m.x >> 3)) + Math.abs((r.y >> 3) - (m.y >> 3))
    if (d < bd) { bd = d; best = r }
  }
  return best
}

// Case du coureur vivant le plus proche (distance de Manhattan en cases), ou -1. Dérivée de
// nearestRunner : STRICTEMENT équivalente à l'ancienne implémentation (même départage, même case).
function nearestRunnerCell(s: GameState, m: Monk): number {
  const r = nearestRunner(s, m)
  return r ? cellAt(r.x, r.y, s.cols) : -1
}

// Ligne de vue (IA maligne) entre deux cases, tracé de Bresenham DÉTERMINISTE. Vrai si AUCUNE case
// INTERMÉDIAIRE (ni la case de départ ni celle d'arrivée) n'est solide (isSolid). La lave et le vide
// ne bloquent PAS la vue (isSolid les exclut) : un moine « voit » par-dessus un lac de lave ou un
// trou. Fonction PURE (aucun RNG, aucune horloge) : réutilisable et testable isolément.
export function hasLineOfSight(s: GameState, fromCell: number, toCell: number): boolean {
  if (fromCell === toCell) return true
  let x = colOf(fromCell, s.cols), y = rowOf(fromCell, s.cols)
  const x1 = colOf(toCell, s.cols), y1 = rowOf(toCell, s.cols)
  const dx = Math.abs(x1 - x), dy = Math.abs(y1 - y)
  const sx = x < x1 ? 1 : -1, sy = y < y1 ? 1 : -1
  let err = dx - dy
  for (;;) {
    const e2 = 2 * err
    if (e2 > -dy) { err -= dy; x += sx }
    if (e2 < dx) { err += dx; y += sy }
    if (x === x1 && y === y1) return true       // arrivé sans obstacle : toutes les intermédiaires étaient dégagées
    if (isSolid(tileAt(s, x, y))) return false  // case intermédiaire solide : vue coupée
  }
}

// Cible « maligne » (smartMonks) d'un moine qui n'est PAS un voleur en fuite. Avec ligne de vue vers
// le coureur le plus proche : on MÉMORISE sa case (lastSeen) et on vise une case EN AVANT de lui
// (anticipation). Sans ligne de vue : PATROUILLE — on rejoint la dernière case vue (lastSeen), et une
// fois dessus sans l'avoir revu on ré-arme lastSeen et on reprend l'aller-retour horizontal par
// défaut. Renvoie TOUJOURS une case ≥ 0 (jamais -1) : même sans coureur vivant le moine patrouille
// (aller-retour) ; il ne « reste sur place » que via patrolTargetCell coincé des deux côtés (sa propre
// case). Purement déterministe (hasLineOfSight est un tracé fixe, aucun RNG).
function smartTargetCell(s: GameState, m: Monk): number {
  const myCell = cellAt(m.x, m.y, s.cols)
  const runner = nearestRunner(s, m)
  if (runner) {
    const runnerCell = cellAt(runner.x, runner.y, s.cols)
    if (hasLineOfSight(s, myCell, runnerCell)) {
      m.lastSeen = runnerCell
      return anticipatedCell(s, runnerCell, runner.face)
    }
  }
  // Pas de ligne de vue (ou plus aucun coureur) : patrouille. On vise la dernière case vue ; arrivé
  // dessus sans revoir le coureur, on la ré-arme (-1) et on bascule sur l'aller-retour horizontal.
  if (m.lastSeen >= 0) {
    if (m.lastSeen !== myCell) return m.lastSeen
    m.lastSeen = -1
  }
  return patrolTargetCell(s, m)
}

// Case anticipée : K = 2 cases DEVANT le coureur dans son sens (runnerFace), colonne clampée à la
// grille (jamais de débordement de rangée par l'arithmétique d'index). Si cette case n'est pas
// praticable pour un moine (bord atteint, mur ou lave = blockedForMonk), on retombe sur la case
// EXACTE du coureur (cible sûre). Un anticipé praticable mais hors d'atteinte du BFS est géré en aval
// par columnFallback (rapprochement de la colonne), donc jamais figé. Pur, aucun RNG.
function anticipatedCell(s: GameState, runnerCell: number, runnerFace: -1 | 1): number {
  const col = colOf(runnerCell, s.cols), row = rowOf(runnerCell, s.cols)
  const ahead = Math.max(0, Math.min(s.cols - 1, col + runnerFace * 2))
  if (ahead === col) return runnerCell // coureur au bord : rien devant, viser sa case
  return blockedForMonk(tileAt(s, ahead, row)) ? runnerCell : cellIndex(ahead, row, s.cols)
}

// Patrouille par défaut (aucune position mémorisée) : aller-retour horizontal. On vise la case
// latérale suivante dans le sens courant (m.face) ; au mur/bord/lave, demi-tour (m.face inversé) et
// on vise l'autre côté. Coincé des deux côtés : on reste sur place. Déterministe ; moveToward
// réalignera ensuite m.face sur le déplacement réel. Ne mute m.face QUE sous smartMonks (chase off
// n'appelle jamais cette fonction) : aucun effet de bord sur le comportement historique.
function patrolTargetCell(s: GameState, m: Monk): number {
  const col = m.x >> 3, row = m.y >> 3
  const canGo = (dir: -1 | 1): boolean => {
    const c = col + dir
    return c >= 0 && c < s.cols && !blockedForMonk(tileAt(s, c, row))
  }
  if (!canGo(m.face)) m.face = m.face === 1 ? -1 : 1 // demi-tour au mur/bord
  if (!canGo(m.face)) return cellIndex(col, row, s.cols)     // coincé des deux côtés : immobile
  return cellIndex(col + m.face, row, s.cols)
}

// Cible de FUITE du voleur charge (Phase B) : une case praticable ELOIGNEE du coureur le plus
// proche, sur la rangee du moine, vers le bord de grille OPPOSE au coureur (maximise la distance
// horizontale, terme dominant de la distance de Manhattan). Le BFS route ensuite le moine vers cette
// case par un chemin praticable ; si le chemin direct est barre, columnFallback le pousse quand meme
// dans la direction de fuite. Purement deterministe (aucun RNG : l'imperfection 1/8 du BFS reste le
// seul alea, appelee comme pour une chasse). On part du bord et on balaie vers l'interieur : premiere
// case NON bloquee (ni mur ni lave), soit la plus eloignee atteignable en ligne sur la rangee.
// Renvoie -1 si aucun coureur vivant (chase ne bouge alors pas, comme pour nearestRunnerCell < 0) ;
// renvoie la case du moine s'il est deja acule contre le bord (BFS from===to -> aucun pas -> immobile).
function fleeTargetCell(s: GameState, m: Monk): number {
  const runner = nearestRunnerCell(s, m)
  if (runner < 0) return -1
  const col = m.x >> 3, row = m.y >> 3, rc = colOf(runner, s.cols)
  const dir: -1 | 1 = rc < col ? 1 : rc > col ? -1 : (col <= (s.cols - 1) / 2 ? 1 : -1)
  const edge = dir === 1 ? s.cols - 1 : 0
  for (let c = edge; c !== col; c -= dir) {
    if (!blockedForMonk(tileAt(s, c, row))) return cellIndex(c, row, s.cols)
  }
  return cellIndex(col, row, s.cols) // acule contre le bord : reste sur place
}

// Sortie d'un trou après TRAP_TICKS (Task 9). Un trou d'1 case sur sol plat ne se
// quitte PAS tout droit : la case juste au-dessus n'a pas d'appui (son plancher est
// le trou ouvert), le moine y retomberait aussitôt. Il remonte donc à la surface
// puis se décale d'une case vers un sol praticable — de préférence vers le coureur —
// et reprend sa poursuite. Sous un plafond solide il patiente (il sera enseveli à la
// repousse). Jamais atteint en Task 8 (aucun moine n'était piégé avant enterHole).
function climbOut(s: GameState, m: Monk) {
  const col = colOf(m.trappedCell, s.cols), row = rowOf(m.trappedCell, s.cols)
  if (row - 1 < 0 || isSolid(tileAt(s, col, row - 1))) return // plafond au-dessus du trou : patiente
  const surfaceY = (row - 1) * SUB
  if (m.y > surfaceY) { m.y -= 1; return } // Phase 1 : remonter dans la colonne du trou
  const exit = exitCellFor(s, m)
  if (exit < 0) { freeMonk(m); return } // aucune sortie latérale (pathologique) : libère tout droit
  const tx = colOf(exit, s.cols) * SUB           // Phase 2 : glisser vers la case-sol de sortie
  if (m.x !== tx) { m.face = m.x < tx ? 1 : -1; m.x += m.face; return }
  freeMonk(m) // posé sur un sol : libéré, il reprendra la poursuite au tick suivant
}

// Fin de blocage : le moine redevient un poursuivant normal (drapeaux remis à zéro).
function freeMonk(m: Monk) { m.trappedCell = -1; m.trappedUntil = 0; m.falling = false }

/** Le joueur est-il en train de reboucher ce trou ? (au moins un coup de pioche donné) */
function rebouchageEnCours(s: GameState, cell: number): boolean {
  const h = s.holes.find((x) => x.cell === cell)
  return !!h && h.fill > 0
}

/** Fin du répit sur un appât : l'appât disparaît (usage unique) et le moine reprend sa chasse.
 *  Appelé aussi à sa mort, sinon un moine tué sur l'appât l'emporterait dans la tombe. */
function libererAppat(s: GameState, m: Monk): void {
  if (m.lureCell < 0) return
  s.appats = s.appats.filter((c) => c !== m.lureCell)
  m.lureCell = -1
  m.lureUntil = 0
}

// DÉLIVRANCE D'UN CAPTIF (31/07, demande Claude-Alain « il peut être libéré par un moine qui
// utilise le marteau-piqueur »). Un congénère VIVANT, porteur du marteau, posté sur une case
// adjacente au trou (les 8 voisines), casse la paroi : le captif repasse en sortie assistée
// (climbOut) et le sauveteur CONSOMME son outil — sans ça, un seul marteau viderait toutes les
// prisons de la carte en boucle.
// ⚠️ Impossible pendant que le joueur REBOUCHE (`fill > 0`) : « le moine ne peut pas interagir
// quand on lance la procédure de rebouchage ». Le joueur qui s'est engagé n'est donc pas pris de
// vitesse au dernier coup de pioche.
function tenterDelivrance(s: GameState, captif: Monk): void {
  if (!s.monkPowers) return // le marteau chez les moines n'existe qu'avec cette option
  const col = colOf(captif.trappedCell, s.cols), row = rowOf(captif.trappedCell, s.cols)
  for (const o of s.monks) {
    if (o === captif || o.respawnAt !== 0 || o.dormant || o.item !== 'jackhammer') continue
    if (o.trappedCell >= 0 || o.emmure) continue // ni un captif ni un emmuré ne délivre un autre
    if (Math.abs((o.x >> 3) - col) > 1 || Math.abs((o.y >> 3) - row) > 1) continue
    o.item = null
    captif.trappedUntil = s.tick // la sortie assistée (climbOut) reprend dès le tick suivant
    s.events.push({ type: 'monkFreed', cell: captif.trappedCell })
    return
  }
}

// Case-sol adjacente (col±1, row-1) où le moine peut sortir du trou : franchissable
// ET portée, en préférant le côté du coureur (sinon il repasserait sur le trou et
// y retomberait). -1 si aucune. L'appui testé porte sur une colonne DIFFÉRENTE du
// trou, donc jamais d'auto-support parasite par le moine encore marqué piégé.
function exitCellFor(s: GameState, m: Monk): number {
  const col = colOf(m.trappedCell, s.cols), row = rowOf(m.trappedCell, s.cols)
  const runner = nearestRunnerCell(s, m)
  const dir = runner < 0 ? m.face : colOf(runner, s.cols) < col ? -1 : colOf(runner, s.cols) > col ? 1 : m.face
  const order = dir < 0 ? [-1, 1] : [1, -1]
  for (const d of order) {
    const cc = col + d, rr = row - 1
    if (cc < 0 || cc >= s.cols || rr < 0 || isSolid(tileAt(s, cc, rr))) continue
    if (hasSupport(s, { x: cc * SUB, y: rr * SUB })) return cellIndex(cc, rr, s.cols)
  }
  return -1
}

// Piégeage du moine dans un trou (Task 9). Il est épinglé au centre de la case-trou
// et immobilisé TRAP_TICKS ticks ; s'il portait une pièce, il la lâche (posée par
// dropCellFor). Le coureur, lui, traverse librement les trous : rien à faire côté
// coureur. Appelé par fallMonk dès que le moine est aligné sur une case-trou (le
// pass-through compte : creuser un pont d'1 brique au-dessus du vide piège bien le moine).
export function enterHole(s: GameState, m: Monk, cell: number) {
  m.x = colOf(cell, s.cols) * SUB
  m.y = rowOf(cell, s.cols) * SUB
  m.falling = false
  m.trappedCell = cell
  // DURÉE DE CAPTIVITÉ (31/07, réglage Claude-Alain « les moines doivent toujours pouvoir essayer
  // de ressortir de manière aléatoire comme avant, mais pas durant le rebouchage »). Elle n'est
  // plus fixe : elle est TIRÉE autour de TRAP_TICKS (2 à 6 s), pour qu'on ne puisse pas compter
  // les secondes et repartir pile à temps. Le tirage passe par rngInt (état partagé) : les trois
  // écrans d'une partie en ligne voient exactement la même durée.
  // Le gel PENDANT un rebouchage n'est pas ici : il vit dans stepMonks, où l'on connaît le trou.
  // MOINES ARMÉS : celui qui porte DÉJÀ le marteau casse la paroi lui-même en un éclair (et le
  // garde) — durée fixe et courte, c'est la contre-mesure historique, inchangée.
  const tirage = Math.floor(TRAP_TICKS * 0.5) + rngInt(s, TRAP_TICKS)
  m.trappedUntil = s.tick + (s.monkPowers && m.item === 'jackhammer' ? MONK_JACK_ESCAPE : tirage)
  s.events.push({ type: 'monkTrapped', cell })
  if (m.carried) {
    m.carried = false
    const drop = dropCellFor(s, cell)
    s.golds.push(drop)
    s.golds.sort((a, b) => a - b)
    s.events.push({ type: 'goldDropped', cell: drop })
  }
}

// Case praticable pour poser une pièce lâchée : dans la grille, tuile non solide,
// libre de toute autre pièce et de tout objet.
function practicable(s: GameState, col: number, row: number): boolean {
  if (col < 0 || col >= s.cols || row < 0 || row >= s.rows) return false
  if (isSolid(tileAt(s, col, row))) return false
  // AUDIT 16/07 : jamais dans une tuile MORTELLE. Un or (ou un objet) déposé dans la lave, le feu,
  // les pics ou une zone mortelle est IRRÉCUPÉRABLE — le coureur meurt avant de le ramasser — et un
  // or requis par la révélation de sortie y scellerait le niveau. L'eau reste permise : on y nage.
  // Sur un niveau sans tuile mortelle, ce test ne retire jamais rien (zéro régression).
  const t = tileAt(s, col, row)
  if (t === T.Lava || t === T.Fire || t === T.DeathZone || t === T.Spikes) return false
  if (!isStableForLoose(s, col, row)) return false // gravité de l'or (11/07) : ne jamais déposer au-dessus du vide
  const cell = cellIndex(col, row, s.cols)
  return !s.golds.includes(cell) && !s.items.some((i) => i.cell === cell)
}

// Emplacement déterministe où retombe la pièce lâchée par un moine piégé (Task 9) :
// voisins immédiats au-dessus/à côté du trou dans un ordre fixe, puis (col,row-2),
// puis balayage de la grille ligne par ligne depuis la ligne du trou vers le haut ;
// première case praticable. Dernier recours (grille pathologiquement pleine) : (col,row-1).
/** La case du coureur VIVANT le plus proche de `m`, ou -1 s'il n'y en a aucun.
 *  ⚠️ Départage par l'ORDRE des coureurs à distance égale, jamais par un tirage : deux écrans d'une
 *  partie en ligne doivent faire tomber l'or au même endroit. */
function coureurLePlusProche(s: GameState, m: Monk): number {
  let best = -1, bestD = Infinity
  for (const r of s.runners) {
    if (!r.alive) continue
    const d = Math.abs(r.x - m.x) + Math.abs(r.y - m.y)
    if (d < bestD) { bestD = d; best = cellAt(r.x, r.y, s.cols) }
  }
  return best
}

/** `n` cases DISTINCTES pour poser une récompense autour de `centre`, en commençant par au-dessus.
 *  Complète par les voisines si la case haute est prise, puis élargit. */
function casesRecompense(s: GameState, centre: number, n: number): number[] {
  const c = colOf(centre, s.cols), r = rowOf(centre, s.cols)
  const seq: [number, number][] = [
    [c, r - 1], [c - 1, r - 1], [c + 1, r - 1],          // la rangée juste au-dessus de la tête
    [c, r - 2], [c - 1, r - 2], [c + 1, r - 2],          // un cran plus haut
    [c - 1, r], [c + 1, r], [c, r],                       // à défaut, à hauteur d'homme
  ]
  const out: number[] = []
  for (const [cc, rr] of seq) {
    if (out.length >= n) break
    if (!practicable(s, cc, rr)) continue
    const cell = cellIndex(cc, rr, s.cols)
    if (!out.includes(cell)) out.push(cell)
  }
  while (out.length < n) out.push(dropCellFor(s, centre))  // repli : mieux vaut empilé que perdu
  return out
}

export function dropCellFor(s: GameState, holeCell: number): number {
  const c = colOf(holeCell, s.cols), r = rowOf(holeCell, s.cols)
  const seq: [number, number][] = [[c, r - 1], [c - 1, r - 1], [c + 1, r - 1], [c - 1, r], [c + 1, r], [c, r - 2]]
  for (const [cc, rr] of seq) if (practicable(s, cc, rr)) return cellIndex(cc, rr, s.cols)
  for (let rr = r; rr >= 0; rr--) for (let cc = 0; cc < s.cols; cc++) if (practicable(s, cc, rr)) return cellIndex(cc, rr, s.cols)
  return cellIndex(c, Math.max(0, r - 1), s.cols)
}

// Mort d'un moine : réapparition différée, drapeaux de piégeage remis à zéro. La pièce
// portée est NORMALEMENT lâchée au piégeage (enterHole) ; dans le cas rare d'un
// ensevelissement sans piégeage préalable, elle disparaît simplement (toléré : le
// niveau reste gagnable, l'or perdu n'est plus requis par la révélation de sortie).
export function killMonk(s: GameState, m: Monk) {
  // MOINES ARMÉS : l'objet porté RETOMBE au sol (récupérable — anti soft-lock : un marteau
  // requis confisqué par un moine se récupère en le tuant). Même logique de case que l'or.
  if (m.item) {
    s.items.push({ cell: dropCellFor(s, cellAt(m.x, m.y, s.cols)), kind: m.item })
    m.item = null
  }
  // Boss « Moine Fou » : encaisse plusieurs coups. Chaque « mort » retire 1 coup (hits) ; tant qu'il
  // lui en reste (hits > 0 après décrément), il SURVIT : il lâche l'or porté (comme killMonkInLava,
  // pour ne pas l'engloutir) puis subit la mort standard ci-dessous (réapparition différée, drapeaux
  // remis à zéro, event monkDied) et REVIENT avec ses coups restants (respawnMonks ne lui rend ses 3
  // coups qu'une fois hits tombé à 0, sa dernière mort). Décrément SEULEMENT pour le boss : le `&&`
  // court-circuite sur m.kind, donc les autres moines gardent hits = 0 et la mort standard INCHANGÉE.
  const bossSurvives = m.kind === 'boss' && --m.hits > 0
  if (bossSurvives && m.carried) {
    m.carried = false
    const to = dropCellFor(s, cellAt(m.x, m.y, s.cols))
    s.golds.push(to)
    s.golds.sort((a, b) => a - b)
    s.events.push({ type: 'goldDropped', cell: to })
  }
  m.respawnAt = s.tick + MONK_RESPAWN_TICKS
  m.trappedCell = -1
  m.trappedUntil = 0
  m.stunUntil = 0 // sinon un moine tazé puis tué (respawn < STUN_TICKS_MONK) réapparaîtrait gelé
  m.bounce = 0    // même raison pour le TRAMPOLINE (30/07) : tué EN PLEIN REBOND, il réapparaîtrait
  m.bounceDir = 0 // en train de monter, et dérivant, depuis son point de spawn
  libererAppat(s, m) // tué SUR un appât : il ne l'emporte pas dans la tombe, il redevient libre
  // OR PORTÉ (Task 3.2) : un moine porteur tué par une mort TERMINALE (bombe, écrasement de caisse,
  // presse/barrière, ensevelissement d'un non-piégé) RELÂCHE sa pièce sur une case stable au lieu de
  // l'engloutir — même geste exact que killMonkInLava / le tazer / enterHole. Le boss qui SURVIT a
  // déjà lâché son or plus haut (m.carried y est remis à false) : ce bloc est alors sauté, donc jamais
  // de double. Le sort() garde l'ordre canonique de s.golds (déterminisme du hash).
  if (m.carried) {
    m.carried = false
    const to = dropCellFor(s, cellAt(m.x, m.y, s.cols))
    s.golds.push(to)
    s.golds.sort((a, b) => a - b)
    s.events.push({ type: 'goldDropped', cell: to })
  }
  m.pathNextCell = -1
  m.hop = 0 // annule un saut de sauteur en cours : sinon le moine réapparu dériverait au lieu de tomber
  m.rampHop = 0; m.rampDy = 0 // idem pour un franchissement de rampe en cours (miroir de r.rampHop = 0 côté coureur)
  const mort = cellAt(m.x, m.y, s.cols)
  s.events.push({ type: 'monkDied', cell: mort })
  // BOSS VAINCU (31/07, demande Claude-Alain « un boss à plusieurs vies »). Le coup qui vide son
  // compteur est le DERNIER : il ne réapparaît plus (respawnMonks saute les `defeated`). Avant ce
  // jour, hits retombait à 3 à la réapparition et frapper trois fois ne valait pas mieux qu'une.
  // La récompense est de l'OR : dans un jeu dont le but est l'or, c'est la seule qui se lise sans
  // explication — et elle rend le détour par le boss rentable au lieu d'être une simple corvée.
  if (m.kind === 'boss' && m.hits <= 0) {
    m.defeated = true
    // OÙ TOMBE L'OR (01/08, demande Claude-Alain « les pièces que droppent les boss doivent pop
    // au-dessus de la tête du joueur »). Il tombait AUTOUR DU BOSS, souvent hors de vue ou dans un
    // recoin qu'on ne repassait pas : la récompense existait sans se voir. Elle apparaît désormais
    // au-dessus de CELUI QUI VIENT DE GAGNER.
    // ⚠️ Et sur TROIS cases distinctes : `dropCellFor` ne réserve rien, donc l'appeler trois fois
    // renvoyait trois fois la même case et les trois pièces se superposaient — on n'en voyait
    // qu'une, et la récompense paraissait trois fois plus petite qu'elle n'est.
    const cible = coureurLePlusProche(s, m)
    for (const c of casesRecompense(s, cible >= 0 ? cible : mort, BOSS_GOLD_REWARD)) s.golds.push(c)
    s.golds.sort((a, b) => a - b)
    s.totalGold += BOSS_GOLD_REWARD // le pot annoncé au bandeau suit, sinon « 12/9 pièces »
    s.events.push({ type: 'bossDown', cell: cible >= 0 ? cible : mort })
  }
}

// Mort d'un moine dans la lave (tuiles P1) : il lâche d'abord la pièce éventuellement
// portée (case praticable stable la plus proche, comme enterHole / le tazer) pour ne pas
// engloutir l'or, PUIS subit la mort de moine standard (killMonk : respawn différé,
// drapeaux remis à zéro, event monkDied). Il réapparaît comme aux autres morts.
function killMonkInLava(s: GameState, m: Monk, burns = true) {
  // MOINES ARMÉS : l'objet porté BRÛLE avec lui (cohérent avec les caisses en lave)... mais SEULEMENT
  // là où quelque chose brûle. AUDIT 16/07 : cette fonction sert aussi aux PICS et à la zone mortelle,
  // où rien ne brûle — l'objet y était pourtant détruit, ce qui pouvait confisquer DÉFINITIVEMENT un
  // marteau requis (killMonk, lui, le relâche exprès pour cette raison). burns=false -> chute normale.
  if (burns) m.item = null
  if (m.carried) {
    m.carried = false
    const to = dropCellFor(s, cellAt(m.x, m.y, s.cols))
    s.golds.push(to)
    s.golds.sort((a, b) => a - b)
    s.events.push({ type: 'goldDropped', cell: to })
  }
  killMonk(s, m)
}

// Réapparition : à respawnAt, ligne 0, colonne praticable tirée par rngInt ; si la
// colonne tirée est solide, scan vers la droite (modulo s.cols).
export function respawnMonks(s: GameState) {
  for (const m of s.monks) {
    if (m.respawnAt === 0 || s.tick < m.respawnAt) continue
    if (m.defeated) continue // BOSS VAINCU (31/07) : il ne revient plus, c'est tout l'intérêt des vies
    // Le tirage est fait DANS TOUS LES CAS, même quand on ne s'en sert pas : le nombre de tirages
    // par tick doit rester constant quel que soit le chemin pris, sinon la suite pseudo-aléatoire
    // diverge entre deux clients qui n'ont pas la même carte en tête (règle rng.ts).
    let col = rngInt(s, s.cols)
    for (let i = 0; i < s.cols; i++) {
      if (!isSolid(tileAt(s, (col + i) % s.cols, 0))) { col = (col + i) % s.cols; break }
    }
    // RÉAPPARITION AU DÉPART (31/07) : le moine revient là où il a été POSÉ dans l'éditeur. On
    // retombe sur la ligne du haut si ce départ est devenu inhabitable (carte d'avant le 31/07
    // dont on ignore le départ, ou case murée depuis — une caisse poussée, une barrière fermée).
    const dep = m.spawnCell
    const utilisable = dep >= 0 && dep < s.cols * s.rows &&
      !isSolid(tileAt(s, colOf(dep, s.cols), rowOf(dep, s.cols))) &&
      !crateAt(s, colOf(dep, s.cols), rowOf(dep, s.cols))
    m.respawnAt = 0
    m.x = utilisable ? colOf(dep, s.cols) * SUB : col * SUB
    m.y = utilisable ? rowOf(dep, s.cols) * SUB : 0
    m.falling = false
    m.pathNextCell = -1
  }
}
